/**
 * Advanced Figma Data Extractors - Main Entry Point
 * 高级 Figma 数据提取器 - 主入口
 */

import type {
  ExtractorFn,
  TraversalContext,
  TraversalOptions,
  GlobalVars,
  StyleTypes,
  SimplifiedDesign,
  SimplifiedNode,
  SimplifiedTextStyle,
  SimplifiedFill,
  SimplifiedLayout,
  SimplifiedStroke,
  SimplifiedEffects,
  ComponentProperties,
  SimplifiedComponentDefinition,
  SimplifiedComponentSetDefinition,
  ProjectionAnalysis,
  ProjectionResult,
  ProjectionComponent,
  BoundingBox,
  FigmaNode,
} from './types';

import { 
  extractFromDesign,
  isVisible,
  generateVarId,
  hasValue 
} from './node-walker';

import { 
  simplifyRawFigmaObject,
  createAdvancedExtractor 
} from './design-extractor';

import {
  layoutExtractor,
  textExtractor,
  visualsExtractor,
  componentExtractor,
  // Convenience combinations
  allExtractors,
  layoutAndText,
  contentOnly,
  visualsOnly,
  layoutOnly,
  componentsOnly,
  // afterChildren helpers
  collapseSvgContainers,
  SVG_ELIGIBLE_TYPES,
} from './built-in';

// Re-export types
export type {
  ExtractorFn,
  TraversalContext,
  TraversalOptions,
  GlobalVars,
  StyleTypes,
  SimplifiedDesign,
  SimplifiedNode,
  SimplifiedTextStyle,
  SimplifiedFill,
  SimplifiedLayout,
  SimplifiedStroke,
  SimplifiedEffects,
  ComponentProperties,
  SimplifiedComponentDefinition,
  SimplifiedComponentSetDefinition,
  ProjectionAnalysis,
  ProjectionResult,
  ProjectionComponent,
  BoundingBox,
  FigmaNode,
};

// Re-export functions
export { 
  extractFromDesign,
  isVisible,
  generateVarId,
  hasValue,
  simplifyRawFigmaObject,
  createAdvancedExtractor,
  layoutExtractor,
  textExtractor,
  visualsExtractor,
  componentExtractor,
  allExtractors,
  layoutAndText,
  contentOnly,
  visualsOnly,
  layoutOnly,
  componentsOnly,
  collapseSvgContainers,
  SVG_ELIGIBLE_TYPES,
};

/**
 * Quick start function for basic extraction
 */
export function quickExtract(figmaData: any, options?: TraversalOptions): SimplifiedDesign {
  return simplifyRawFigmaObject(figmaData, allExtractors, options);
}

/**
 * Advanced extraction with projection analysis
 */
export function advancedExtract(figmaData: any, options?: TraversalOptions): SimplifiedDesign {
  const extractors = [...allExtractors, createAdvancedExtractor()];
  return simplifyRawFigmaObject(figmaData, extractors, options);
}

/**
 * Content-focused extraction (text and layout only)
 */
export function contentExtract(figmaData: any, options?: TraversalOptions): SimplifiedDesign {
  return simplifyRawFigmaObject(figmaData, layoutAndText, options);
}

/**
 * Style-focused extraction (visuals only)
 */
export function styleExtract(figmaData: any, options?: TraversalOptions): SimplifiedDesign {
  return simplifyRawFigmaObject(figmaData, visualsOnly, options);
}

/**
 * Component-focused extraction
 */
export function componentExtract(figmaData: any, options?: TraversalOptions): SimplifiedDesign {
  return simplifyRawFigmaObject(figmaData, [componentExtractor], options);
}

/**
 * Create custom extraction pipeline
 */
export function createCustomExtractor(extractors: ExtractorFn[], options?: TraversalOptions) {
  return (figmaData: any): SimplifiedDesign => simplifyRawFigmaObject(figmaData, extractors, options);
}

/**
 * Utility function to merge extraction results
 */
export function mergeExtractionResults(...results: SimplifiedDesign[]): SimplifiedDesign {
  if (results.length === 0) {
    throw new Error('No results to merge');
  }

  const base = results[0];
  const merged: SimplifiedDesign = {
    name: base.name,
    nodes: [...base.nodes],
    components: { ...base.components },
    componentSets: { ...base.componentSets },
    globalVars: { styles: { ...base.globalVars.styles } },
  };

  // Merge additional results
  for (let i = 1; i < results.length; i++) {
    const result = results[i];
    
    // Merge nodes (avoid duplicates by ID)
    const existingNodeIds = new Set(merged.nodes.map((n: SimplifiedNode) => n.id));
    for (const node of result.nodes) {
      if (!existingNodeIds.has(node.id)) {
        merged.nodes.push(node);
      }
    }
    
    // Merge components
    Object.assign(merged.components, result.components);
    Object.assign(merged.componentSets, result.componentSets);
    
    // Merge global styles
    Object.assign(merged.globalVars.styles, result.globalVars.styles);
  }

  return merged;
}

/**
 * Utility function to filter extraction results
 */
export function filterExtractionResult(
  result: SimplifiedDesign,
  filter: {
    nodeTypes?: string[];
    nodeFilter?: (node: SimplifiedNode) => boolean;
    includeStyles?: boolean;
    includeComponents?: boolean;
  }
): SimplifiedDesign {
  const filtered: SimplifiedDesign = {
    name: result.name,
    nodes: result.nodes,
    components: filter.includeComponents !== false ? result.components : {},
    componentSets: filter.includeComponents !== false ? result.componentSets : {},
    globalVars: filter.includeStyles !== false ? result.globalVars : { styles: {} },
  };

  // Filter nodes by type
  if (filter.nodeTypes) {
    const allowedTypes = new Set(filter.nodeTypes);
    filtered.nodes = filterNodesByType(result.nodes, allowedTypes);
  }

  // Filter nodes by custom function
  if (filter.nodeFilter) {
    filtered.nodes = filterNodesRecursive(filtered.nodes, filter.nodeFilter);
  }

  return filtered;
}

/**
 * Helper function to filter nodes by type recursively
 */
function filterNodesByType(nodes: SimplifiedNode[], allowedTypes: Set<string>): SimplifiedNode[] {
  return nodes
    .filter(node => allowedTypes.has(node.type))
    .map(node => ({
      ...node,
      children: node.children ? filterNodesByType(node.children, allowedTypes) : undefined,
    }));
}

/**
 * Helper function to filter nodes recursively
 */
function filterNodesRecursive(nodes: SimplifiedNode[], filter: (node: SimplifiedNode) => boolean): SimplifiedNode[] {
  return nodes
    .filter(filter)
    .map(node => ({
      ...node,
      children: node.children ? filterNodesRecursive(node.children, filter) : undefined,
    }));
}

/**
 * Utility function to get extraction statistics
 */
export function getExtractionStats(result: SimplifiedDesign) {
  const stats = {
    totalNodes: 0,
    nodesByType: {} as Record<string, number>,
    totalComponents: Object.keys(result.components).length,
    totalComponentSets: Object.keys(result.componentSets).length,
    totalStyles: Object.keys(result.globalVars.styles).length,
    maxDepth: 0,
  };

  function analyzeNode(node: SimplifiedNode, depth = 0) {
    stats.totalNodes++;
    stats.nodesByType[node.type] = (stats.nodesByType[node.type] || 0) + 1;
    stats.maxDepth = Math.max(stats.maxDepth, depth);

    if (node.children) {
      for (const child of node.children) {
        analyzeNode(child, depth + 1);
      }
    }
  }

  for (const node of result.nodes) {
    analyzeNode(node);
  }

  return stats;
}